<script setup lang="ts">
    import { throttle } from '@/utils/throttle';
    import { isTouchDevice } from '@/utils/deviceDetection';
    import { THROTTLE_MOUSE_TIMING, THROTTLE_TOUCH_TIMING } from '@/config';
    import { computed, onMounted, onUpdated, ref } from 'vue';

    const {
        labelId,
        max,
        min,
        modelValue,
        showValue = true,
    } = defineProps<{
        labelId: string; // used by aria-labelledby for accessibility
        max: string;
        min: string;
        modelValue: number;
        showValue?: boolean;
    }>();

    const emit = defineEmits<{
        'update:modelValue': [number];
    }>();

    // eslint-disable-next-line vue/prefer-use-template-ref
    const sliderButton = ref<HTMLElement | null>(null);
    const isDragging = ref(false);
    const buttonPosition = ref('0%');
    // eslint-disable-next-line vue/prefer-use-template-ref
    const sliderBar = ref<HTMLElement | null>(null);
    let animationFrameId: number | null = null;

    const minNumber = computed(() => Number(min));
    const maxNumber = computed(() => Number(max));
    const sliderValue = computed(() => Math.min(Math.max(modelValue, minNumber.value), maxNumber.value)); // Clamp the value to the boundaries

    // Optimized throttled update - faster for touch devices
    const throttledUpdateSliderValue = throttle(updateSliderValue, isTouchDevice() ? THROTTLE_TOUCH_TIMING : THROTTLE_MOUSE_TIMING, {
        throttleTimer: false,
    });

    function updateSliderValue(value: number) {
        emit('update:modelValue', Math.round(value));
    }

    function updateButtonPosition(value: number) {
        const percentage = ((value - minNumber.value) / (maxNumber.value - minNumber.value)) * 100;
        buttonPosition.value = `${percentage}%`;
    }

    function refreshPosition(value: number) {
        updateSliderValue(value);
        updateButtonPosition(value);
    }

    function refreshPositionWhileDragging(value: number) {
        // Mobile optimization: skip requestAnimationFrame for touch devices for immediate response
        if (isTouchDevice()) {
            throttledUpdateSliderValue(value);
            updateButtonPosition(value);
        } else {
            // Desktop: use requestAnimationFrame for smooth animation
            if (animationFrameId) {
                cancelAnimationFrame(animationFrameId);
            }

            animationFrameId = requestAnimationFrame(() => {
                throttledUpdateSliderValue(value);
                updateButtonPosition(value);
                animationFrameId = null;
            });
        }
    }

    function getPositionForEvent(event: MouseEvent | TouchEvent) {
        const sliderRect = sliderBar.value?.getBoundingClientRect();
        const sliderLeft = sliderRect?.left || 0;
        const sliderWidth = sliderRect?.width || 0;

        const touchClientX = 'touches' in event ? event.touches[0]?.clientX || event.changedTouches[0]?.clientX || 0 : 0;
        const clickClientX = 'touches' in event ? 0 : event.clientX;
        const clientX = 'touches' in event ? touchClientX : clickClientX;

        const offsetX = clientX - sliderLeft;
        return (offsetX / sliderWidth) * (maxNumber.value - minNumber.value) + minNumber.value;
    }

    function handleDragging(event: MouseEvent | TouchEvent) {
        if (!isDragging.value) {
            return;
        }

        // Prevent scrolling on touch devices while dragging
        if ('touches' in event) {
            event.preventDefault();
            // Skip stopPropagation on mobile for better performance
        }

        const position = getPositionForEvent(event);
        refreshPositionWhileDragging(position);
    }

    function onSliderBarClick(event: MouseEvent | TouchEvent) {
        // Prevent default behavior for touch events
        if ('touches' in event) {
            event.preventDefault();
        }

        const position = getPositionForEvent(event);
        refreshPosition(position);

        sliderButton.value?.focus();
    }

    function onSliderBarTouch(event: TouchEvent) {
        const position = getPositionForEvent(event);
        refreshPosition(position);

        sliderButton.value?.focus();
    }

    function startDragging(event?: MouseEvent | TouchEvent) {
        // Prevent default for touch events to avoid conflicts
        if (event && 'touches' in event) {
            event.preventDefault();
        }

        isDragging.value = true;

        document.addEventListener('touchmove', handleDragging);
        document.addEventListener('touchend', stopDragging);
        document.addEventListener('touchcancel', stopDragging);
        document.addEventListener('mousemove', handleDragging);
        document.addEventListener('mouseup', stopDragging);
        document.addEventListener('touchmove', handleDragging);
        document.addEventListener('touchend', stopDragging);
        document.addEventListener('touchcancel', stopDragging);
    }

    function stopDragging() {
        isDragging.value = false;

        // Cancel any pending animation frame (desktop only)
        if (animationFrameId) {
            cancelAnimationFrame(animationFrameId);
            animationFrameId = null;
        }

        // Remove all possible event listeners
        document.removeEventListener('mousemove', handleDragging);
        document.removeEventListener('mouseup', stopDragging);
        document.removeEventListener('touchmove', handleDragging);
        document.removeEventListener('touchend', stopDragging);
        document.removeEventListener('touchcancel', stopDragging);
    }

    function goToFirstValue() {
        refreshPosition(minNumber.value);
    }

    function goToLastValue() {
        refreshPosition(maxNumber.value);
    }

    function goToPreviousValue() {
        const newValue = sliderValue.value - 1;
        refreshPosition(newValue);
    }

    function goToNextValue() {
        const newValue = sliderValue.value + 1;
        refreshPosition(newValue);
    }

    onMounted(() => {
        refreshPosition(sliderValue.value);
    });

    onUpdated(() => {
        refreshPosition(sliderValue.value);
    });
</script>

<template>
    <!-- 15px = 30px / 2 (size of the draggable button) : we move the bar of half the width of the button on the right -->
    <!-- 13px = 30px / 2 (size of the draggable button) - 4px / 2 (size of the bar) : we center the button vertically on the bar -->
    <!-- We set a padding of 30px to the right because the bar is offset of 15px on the right (for centering the button on boundaries) -->
    <div class="relative w-56 touch-none p-[15px] pr-[30px]">
        <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/no-static-element-interactions -->
        <div
            class="absolute left-0 top-0 w-full cursor-pointer py-[13px]"
            @click="onSliderBarClick"
            @touchstart.prevent="onSliderBarTouch"
        >
            <div
                ref="sliderBar"
                data-test-id="slider-bar"
                class="left-[15px] h-full w-full touch-none border-2 border-secondary bg-white drop-shadow-lg"
            />
        </div>
        <button
            ref="sliderButton"
            class="absolute top-1 z-10 h-[20px] w-[20px] touch-none rounded-full border-[2px] border-black bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50"
            :style="{ left: buttonPosition }"
            data-test-id="slider-button"
            role="slider"
            :aria-valuenow="sliderValue"
            :aria-valuemin="min"
            :aria-valuemax="max"
            :aria-labelledby="labelId"
            aria-orientation="horizontal"
            tabindex="0"
            :aria-description="$t('slider_accessibility_action')"
            @mousedown="startDragging"
            @touchstart.prevent="startDragging"
            @keydown.home="goToFirstValue"
            @keydown.end="goToLastValue"
            @keydown.arrow-left="goToPreviousValue"
            @keydown.arrow-down="goToPreviousValue"
            @keydown.arrow-right="goToNextValue"
            @keydown.arrow-up="goToNextValue"
        >
            <span
                v-if="showValue"
                data-test-id="slider-value"
                class="absolute left-0 right-0 top-0 mt-8 flex items-center justify-center text-xl font-medium text-black"
            >
                {{ sliderValue }}
            </span>
        </button>
    </div>
</template>
